<template>
  <div class="vacaciones-calendario">
    <!-- Hero con resumen y selector de año -->
    <div class="vac-hero">
      <div class="vac-hero__head">
        <div class="vac-hero__icon">
          <feather-icon icon="SunIcon" size="20" />
        </div>
        <div class="vac-hero__title">
          <h4 class="vac-hero__heading">Mis vacaciones</h4>
          <span class="vac-hero__sub">Planifica tus días libres</span>
        </div>
      </div>

      <div class="vac-hero__main">
        <div class="vac-hero__main-top">
          <span class="vac-hero__main-num">{{ resumenDias.disponibles }}</span>
          <span class="vac-hero__main-label">días disponibles</span>
        </div>
        <div class="vac-progress">
          <div class="vac-progress__fill" :style="{ width: porcentajeUsado + '%' }"></div>
        </div>
        <div class="vac-progress__meta">
          <span>{{ resumenDias.aprobados + resumenDias.pendientes }} de {{ resumenDias.totales }} usados</span>
          <span>{{ porcentajeUsado }}%</span>
        </div>
      </div>

      <div class="vac-hero__metrics">
        <div class="vac-metric">
          <feather-icon icon="CheckCircleIcon" size="16" />
          <div class="vac-metric__txt">
            <span class="vac-metric__num">{{ resumenDias.aprobados }}</span>
            <span class="vac-metric__label">Aprobados</span>
          </div>
        </div>
        <div class="vac-metric">
          <feather-icon icon="ClockIcon" size="16" />
          <div class="vac-metric__txt">
            <span class="vac-metric__num">{{ resumenDias.pendientes }}</span>
            <span class="vac-metric__label">Pendientes</span>
          </div>
        </div>
        <div class="vac-metric">
          <feather-icon icon="CalendarIcon" size="16" />
          <div class="vac-metric__txt">
            <span class="vac-metric__num">{{ resumenDias.totales }}</span>
            <span class="vac-metric__label">Totales</span>
          </div>
        </div>
      </div>

      <div class="vac-year-switch">
        <button class="vac-year-switch__btn" :disabled="!puedeAnioAnterior" @click="cambiarAnio(-1)" aria-label="Año anterior">
          <feather-icon icon="ChevronLeftIcon" size="16" />
        </button>
        <span class="vac-year-switch__value">{{ anioSeleccionado }}</span>
        <button class="vac-year-switch__btn" :disabled="!puedeAnioSiguiente" @click="cambiarAnio(1)" aria-label="Año siguiente">
          <feather-icon icon="ChevronRightIcon" size="16" />
        </button>
      </div>
    </div>

    <!-- Alerta si no hay calendario asignado -->
    <b-alert v-if="!miCalendario" show variant="warning" class="mb-2">
      <div class="alert-body">
        <feather-icon icon="AlertTriangleIcon" class="mr-50" />
        No tienes calendario asignado para el año {{ anioSeleccionado }}. Contacta con tu administrador para poder solicitar vacaciones.
      </div>
    </b-alert>

    <!-- Toolbar: filtros + acción -->
    <div class="vac-toolbar">
      <div class="vac-toolbar__filters">
        <span class="vac-toolbar__label">Mostrar</span>
        <button
          class="vac-pill vac-pill--equipo"
          :class="{ 'vac-pill--active': mostrarEquipo }"
          @click="toggleEquipo"
        >
          <span class="vac-pill__dot" style="background:#6366f1"></span>
          Equipo
        </button>
        <button
          class="vac-pill vac-pill--tareas"
          :class="{ 'vac-pill--active': mostrarTareas }"
          @click="toggleTareas"
        >
          <span class="vac-pill__dot" style="background:#14b8a6"></span>
          Tareas
        </button>
      </div>
      <div v-if="miCalendario" class="vac-toolbar__hint">
        <feather-icon icon="MousePointerIcon" size="13" class="mr-25" />
        Haz clic en los días del calendario para solicitar vacaciones
      </div>
    </div>

    <!-- Tabs: Calendario y Solicitudes -->
    <b-card no-body>
      <b-tabs card>
        <!-- Tab Calendario -->
        <b-tab active>
          <template #title>
            <feather-icon icon="CalendarIcon" class="mr-50" />
            Calendario
          </template>

          <full-calendar ref="refCalendar" :options="calendarOptions" class="full-calendar" />

          <!-- Leyenda -->
          <div class="vac-legend">
            <div v-if="miCalendario" class="vac-legend__group">
              <span class="vac-legend__title">
                <feather-icon icon="UserIcon" size="11" />
                Mis vacaciones
              </span>
              <div class="vac-legend__items">
                <span class="vac-legend__item"><span class="vac-legend__color" style="background:#28c76f"></span>Aprobadas</span>
                <span class="vac-legend__item"><span class="vac-legend__color" style="background:#00cfe8"></span>Medio día</span>
                <span class="vac-legend__item"><span class="vac-legend__color" style="background:#ff9f43"></span>Pendientes</span>
                <span class="vac-legend__item"><span class="vac-legend__color" style="background:#ea5455"></span>Rechazadas</span>
              </div>
            </div>
            <div v-if="mostrarEquipo" class="vac-legend__group">
              <span class="vac-legend__title">
                <feather-icon icon="UsersIcon" size="11" />
                Equipo
              </span>
              <div class="vac-legend__items">
                <span class="vac-legend__item"><span class="vac-legend__color" style="background:#6366f1"></span>Vacaciones</span>
                <span class="vac-legend__item"><span class="vac-legend__color" style="background:#a855f7"></span>Festivos</span>
              </div>
            </div>
            <div v-if="mostrarTareas" class="vac-legend__group">
              <span class="vac-legend__title">
                <feather-icon icon="CheckSquareIcon" size="11" />
                Otros
              </span>
              <div class="vac-legend__items">
                <span class="vac-legend__item"><span class="vac-legend__color" style="background:#14b8a6"></span>Tareas pendientes</span>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Tab Mis Solicitudes -->
        <b-tab v-if="miCalendario">
          <template #title>
            <feather-icon icon="ListIcon" class="mr-50" />
            Mis Solicitudes
          </template>

          <b-table
            :items="misSolicitudes"
            :fields="camposSolicitudes"
            responsive
            show-empty
            empty-text="No hay solicitudes"
          >
            <template #cell(fecha)="data">
              {{ formatearFechaLarga(data.item.fecha) }}
            </template>

            <template #cell(tipo)="data">
              <span class="text-capitalize">{{ data.item.tipo }}</span>
            </template>

            <template #cell(estado)="data">
              <b-badge :variant="getEstadoVariant(data.item.estado)">
                {{ data.item.estado }}
              </b-badge>
            </template>

            <template #cell(acciones)="data">
              <b-button
                v-if="data.item.estado === 'pendiente'"
                variant="flat-danger"
                size="sm"
                @click="cancelar(data.item.id)"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </template>
          </b-table>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Botón flotante cuando hay días seleccionados -->
    <transition name="vac-fab">
      <div v-if="miCalendario && fechasSeleccionadas.length > 0" class="vac-fab">
        <div class="vac-fab__left">
          <span class="vac-fab__count">{{ fechasSeleccionadas.length }}</span>
          <span class="vac-fab__label">día{{ fechasSeleccionadas.length === 1 ? '' : 's' }} seleccionado{{ fechasSeleccionadas.length === 1 ? '' : 's' }}</span>
          <button class="vac-fab__clear" @click="fechasSeleccionadas = []">
            <feather-icon icon="XIcon" size="12" />
            Deseleccionar
          </button>
        </div>
        <button class="vac-fab__action" @click="mostrarModalSolicitud = true">
          <feather-icon icon="PlusIcon" size="14" />
          Pedir vacaciones
        </button>
      </div>
    </transition>

    <!-- Modal para pedir vacaciones -->
    <b-modal
      v-model="mostrarModalSolicitud"
      title="Solicitar Vacaciones"
      ok-title="Enviar solicitud"
      cancel-title="Cancelar"
      centered
      @ok="enviarSolicitud"
      :ok-disabled="fechasSeleccionadas.length === 0"
    >
      <b-form-group label="Fechas seleccionadas" label-for="fechas">
        <div v-if="fechasSeleccionadas.length === 0" class="text-muted">
          Haz clic en los dias del calendario para seleccionarlos
        </div>
        <div v-else>
          <b-badge
            v-for="(fecha, index) in fechasSeleccionadas"
            :key="index"
            variant="light-primary"
            class="mr-50 mb-50"
            style="cursor: pointer"
            @click="quitarFecha(index)"
          >
            {{ formatearFecha(fecha) }}
            <feather-icon icon="XIcon" size="12" class="ml-25" />
          </b-badge>
        </div>
      </b-form-group>

      <b-form-group label="Tipo de vacaciones" label-for="tipo">
        <b-form-radio-group v-model="tipoSolicitud" :options="opcionesTipo" name="tipo" />
      </b-form-group>

      <b-alert show variant="info" class="mb-0">
        <small>
          <strong>{{ fechasSeleccionadas.length }}</strong> dia(s) seleccionado(s) =
          <strong>{{ tipoSolicitud === 'completo' ? fechasSeleccionadas.length : fechasSeleccionadas.length * 0.5 }}</strong> dia(s) de vacaciones
        </small>
      </b-alert>
    </b-modal>

    <!-- Modal detalle de evento -->
    <b-modal
      v-model="mostrarModalDetalle"
      :title="eventoSeleccionado ? eventoSeleccionado.title : ''"
      ok-only
      ok-title="Cerrar"
      centered
    >
      <div v-if="eventoSeleccionado">
        <p><strong>Fecha:</strong> {{ formatearFecha(eventoSeleccionado.start) }}</p>
        <p v-if="eventoSeleccionado.extendedProps.tipo === 'solicitud'">
          <strong>Estado:</strong>
          <b-badge :variant="getEstadoVariant(eventoSeleccionado.extendedProps.estado)">
            {{ eventoSeleccionado.extendedProps.estado }}
          </b-badge>
        </p>
        <p v-if="eventoSeleccionado.extendedProps.tipo === 'solicitud'">
          <strong>Tipo:</strong> {{ eventoSeleccionado.extendedProps.tipoVacacion }}
        </p>
        <b-alert
          v-if="eventoSeleccionado.extendedProps.estado === 'rechazada' && eventoSeleccionado.extendedProps.motivo"
          show
          variant="danger"
          class="mt-1 mb-0"
        >
          <strong>Motivo:</strong> {{ eventoSeleccionado.extendedProps.motivo }}
        </b-alert>
        <b-button
          v-if="eventoSeleccionado.extendedProps.tipo === 'solicitud' && eventoSeleccionado.extendedProps.estado === 'pendiente'"
          variant="outline-danger"
          size="sm"
          @click="cancelar(eventoSeleccionado.extendedProps.solicitudId)"
        >
          <feather-icon icon="XIcon" class="mr-50" />
          Cancelar solicitud
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BButton, BModal, BFormGroup, BFormRadioGroup,
  BBadge, BAlert, BTabs, BTab, BTable
} from 'bootstrap-vue'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import { mapState, mapGetters, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BButton,
    BModal,
    BFormGroup,
    BFormRadioGroup,
    BBadge,
    BAlert,
    BTabs,
    BTab,
    BTable,
    FullCalendar,
  },

  data() {
    return {
      anioSeleccionado: new Date().getFullYear(),
      mostrarModalSolicitud: false,
      mostrarModalDetalle: false,
      eventoSeleccionado: null,
      fechasSeleccionadas: [],
      tipoSolicitud: 'completo',
      opcionesTipo: [
        { text: 'Dia completo', value: 'completo' },
        { text: 'Solo mañana', value: 'manana' },
        { text: 'Solo tarde', value: 'tarde' },
      ],
      camposSolicitudes: [
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'tipo', label: 'Tipo' },
        { key: 'estado', label: 'Estado' },
        { key: 'acciones', label: '' },
      ],
      mostrarEquipo: true,
      mostrarTareas: true,
    }
  },

  computed: {
    ...mapState('vacaciones', ['miCalendario', 'misFestivos', 'misSolicitudes', 'miResumen', 'loading', 'vacacionesEquipo', 'festivosEquipo', 'tareasCalendario']),
    ...mapGetters('vacaciones', ['eventosCalendario']),

    resumenDias() {
      return {
        totales: (this.miResumen && this.miResumen.dias_totales) || 0,
        aprobados: (this.miResumen && this.miResumen.dias_aprobados) || 0,
        pendientes: (this.miResumen && this.miResumen.dias_pendientes) || 0,
        disponibles: (this.miResumen && this.miResumen.dias_disponibles) || 0,
      }
    },

    aniosDisponibles() {
      const actual = new Date().getFullYear()
      return [actual - 1, actual, actual + 1].map(a => ({ value: a, text: a.toString() }))
    },

    porcentajeUsado() {
      const total = this.resumenDias.totales
      if (!total) return 0
      const usado = this.resumenDias.aprobados + this.resumenDias.pendientes
      return Math.min(100, Math.round((usado / total) * 100))
    },

    puedeAnioAnterior() {
      return this.anioSeleccionado > new Date().getFullYear() - 1
    },

    puedeAnioSiguiente() {
      return this.anioSeleccionado < new Date().getFullYear() + 1
    },

    eventosConSeleccion() {
      var eventos = this.eventosCalendario.slice()

      // Añadir días seleccionados como eventos de fondo
      this.fechasSeleccionadas.forEach(fecha => {
        eventos.push({
          id: `seleccion-${fecha}`,
          start: fecha,
          display: 'background',
          backgroundColor: 'rgba(115, 103, 240, 0.25)',
        })
      })

      var usuariosTaller = [22,34,35,36,37,38,39,40,41]

      // Vacaciones del equipo
      if (this.mostrarEquipo && this.vacacionesEquipo.length > 0) {
        // Agrupar por fecha para detectar días con todo el taller
        var porFecha = {}
        this.vacacionesEquipo.forEach(function(v) {
          var fecha = typeof v.fecha === 'string' ? v.fecha.split('T')[0] : v.fecha
          if (!porFecha[fecha]) porFecha[fecha] = []
          porFecha[fecha].push(v)
        })

        var self = this
        Object.keys(porFecha).forEach(function(fecha) {
          var items = porFecha[fecha]
          var idsTaller = items.filter(function(v) { return usuariosTaller.indexOf(v.usuario_id) !== -1 }).map(function(v) { return v.usuario_id })
          var todoTaller = usuariosTaller.every(function(id) { return idsTaller.indexOf(id) !== -1 })

          if (todoTaller) {
            // Un solo evento "TALLER" + los que no son del taller individualmente
            eventos.push({
              id: 'equipo-taller-' + fecha,
              title: 'TALLER',
              start: fecha,
              allDay: true,
              classNames: ['evento-equipo', 'evento-taller'],
              extendedProps: { tipo: 'equipo', usuario: 'TALLER' },
              backgroundColor: '#6366f1',
              borderColor: '#6366f1',
            })
            items.forEach(function(v) {
              if (usuariosTaller.indexOf(v.usuario_id) === -1) {
                eventos.push({
                  id: 'equipo-' + v.id,
                  title: v.usuario_nombre,
                  start: fecha,
                  allDay: true,
                  classNames: ['evento-equipo'],
                  extendedProps: { tipo: 'equipo', usuario: v.usuario_nombre },
                  backgroundColor: '#6366f1',
                  borderColor: '#6366f1',
                })
              }
            })
          } else {
            items.forEach(function(v) {
              eventos.push({
                id: 'equipo-' + v.id,
                title: v.usuario_nombre,
                start: fecha,
                allDay: true,
                classNames: ['evento-equipo'],
                extendedProps: { tipo: 'equipo', usuario: v.usuario_nombre },
                backgroundColor: '#6366f1',
                borderColor: '#6366f1',
              })
            })
          }
        })
      }

      // Festivos del equipo (agrupar taller si todos están)
      if (this.mostrarEquipo && this.festivosEquipo.length > 0) {
        var porFechaFestivo = {}
        this.festivosEquipo.forEach(function(f) {
          var fecha = typeof f.fecha === 'string' ? f.fecha.split('T')[0] : f.fecha
          if (!porFechaFestivo[fecha]) porFechaFestivo[fecha] = []
          porFechaFestivo[fecha].push(f)
        })

        Object.keys(porFechaFestivo).forEach(function(fecha) {
          var items = porFechaFestivo[fecha]
          var idsTaller = items.filter(function(f) { return usuariosTaller.indexOf(f.usuario_id) !== -1 }).map(function(f) { return f.usuario_id })
          var todoTaller = usuariosTaller.every(function(id) { return idsTaller.indexOf(id) !== -1 })

          if (todoTaller) {
            eventos.push({
              id: 'festivo-equipo-taller-' + fecha,
              title: 'TALLER',
              start: fecha,
              allDay: true,
              classNames: ['evento-equipo', 'evento-taller'],
              extendedProps: { tipo: 'festivo-equipo', usuario: 'TALLER', festivo: items[0].festivo_nombre },
              backgroundColor: '#a855f7',
              borderColor: '#a855f7',
            })
            items.forEach(function(f) {
              if (usuariosTaller.indexOf(f.usuario_id) === -1) {
                eventos.push({
                  id: 'festivo-equipo-' + f.id,
                  title: f.usuario_nombre,
                  start: fecha,
                  allDay: true,
                  classNames: ['evento-equipo'],
                  extendedProps: { tipo: 'festivo-equipo', usuario: f.usuario_nombre, festivo: f.festivo_nombre },
                  backgroundColor: '#a855f7',
                  borderColor: '#a855f7',
                })
              }
            })
          } else {
            items.forEach(function(f) {
              eventos.push({
                id: 'festivo-equipo-' + f.id,
                title: f.usuario_nombre,
                start: fecha,
                allDay: true,
                classNames: ['evento-equipo'],
                extendedProps: { tipo: 'festivo-equipo', usuario: f.usuario_nombre, festivo: f.festivo_nombre },
                backgroundColor: '#a855f7',
                borderColor: '#a855f7',
              })
            })
          }
        })
      }

      // Tareas pendientes con fecha
      if (this.mostrarTareas && this.tareasCalendario.length > 0) {
        this.tareasCalendario.forEach(t => {
          const fecha = typeof t.fecha_vencimiento === 'string' ? t.fecha_vencimiento.split('T')[0] : t.fecha_vencimiento
          eventos.push({
            id: `tarea-${t.id}`,
            title: t.titulo,
            start: fecha,
            allDay: true,
            extendedProps: { tipo: 'tarea', prioridad: t.prioridad, bloque: t.bloque_nombre },
            backgroundColor: '#14b8a6',
            borderColor: '#14b8a6',
          })
        })
      }

      return eventos
    },

    calendarOptions() {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: esLocale,
        headerToolbar: {
          start: 'prev,next today',
          center: 'title',
          end: '',
        },
        height: 'auto',
        events: this.eventosConSeleccion,
        dateClick: this.handleDateClick,
        eventClick: this.handleEventClick,
        dayCellClassNames: this.getDayCellClass,
        dayCellDidMount: this.handleDayCellMount,
        eventDidMount: this.handleEventMount,
        eventOrder: function(a, b) {
          var aFestivo = a.extendedProps && a.extendedProps.tipo === 'festivo' ? 0 : 1
          var bFestivo = b.extendedProps && b.extendedProps.tipo === 'festivo' ? 0 : 1
          return aFestivo - bFestivo
        },
      }
    },

    festivosEquipoPorFecha() {
      if (!this.mostrarEquipo || this.festivosEquipo.length === 0) return {}
      var map = {}
      this.festivosEquipo.forEach(function(f) {
        var fecha = typeof f.fecha === 'string' ? f.fecha.split('T')[0] : f.fecha
        if (!map[fecha]) {
          map[fecha] = f.festivo_nombre
        }
      })
      return map
    },

    misFestivosPorFecha() {
      var map = {}
      this.misFestivos.forEach(function(f) {
        var fecha = typeof f.fecha === 'string' ? f.fecha.split('T')[0] : f.fecha
        map[fecha] = f.nombre || true
      })
      return map
    },
  },

  watch: {
    festivosEquipoPorFecha() {
      this.$nextTick(function() { this.pintarFestivosEquipo() })
    },
    misFestivosPorFecha() {
      this.$nextTick(function() { this.pintarFestivosEquipo() })
    },
    mostrarEquipo() {
      this.$nextTick(function() { this.pintarFestivosEquipo() })
    },
  },

  mounted() {
    this.cargarCalendario()
  },

  methods: {
    ...mapActions('vacaciones', ['fetchMiCalendario', 'crearSolicitud', 'cancelarSolicitud', 'fetchVacacionesEquipo', 'fetchTareasCalendario']),

    async cargarCalendario() {
      try {
        await this.fetchMiCalendario(this.anioSeleccionado)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error al cargar calendario',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
      this.cargarDatosExtra()
    },

    cargarDatosExtra() {
      if (this.mostrarEquipo) {
        this.fetchVacacionesEquipo(this.anioSeleccionado)
      }
      if (this.mostrarTareas) {
        this.fetchTareasCalendario(this.anioSeleccionado)
      }
    },

    cambiarAnio(delta) {
      const actual = new Date().getFullYear()
      const nuevo = this.anioSeleccionado + delta
      if (nuevo < actual - 1 || nuevo > actual + 1) return
      this.anioSeleccionado = nuevo
      this.cargarCalendario()
    },

    toggleEquipo() {
      this.mostrarEquipo = !this.mostrarEquipo
      this.cargarDatosExtra()
    },

    toggleTareas() {
      this.mostrarTareas = !this.mostrarTareas
      this.cargarDatosExtra()
    },

    handleDateClick(info) {
      // Si no hay calendario asignado, no se puede seleccionar días
      if (!this.miCalendario) return

      // Bloquear fines de semana
      const diaSemana = info.date.getDay()
      if (diaSemana === 0 || diaSemana === 6) {
        return // Sábado (6) o domingo (0)
      }

      const esFestivo = this.misFestivos.some(f => {
        const fechaFestivo = typeof f.fecha === 'string' ? f.fecha.split('T')[0] : f.fecha
        return fechaFestivo === info.dateStr
      })
      if (esFestivo) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No puedes seleccionar un dia festivo',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // Bloquear días con solicitud aprobada o pendiente
      const tieneSolicitud = this.misSolicitudes.some(s => {
        const fechaSolicitud = typeof s.fecha === 'string' ? s.fecha.split('T')[0] : s.fecha
        return fechaSolicitud === info.dateStr && (s.estado === 'aprobada' || s.estado === 'pendiente')
      })
      if (tieneSolicitud) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Ya tienes una solicitud para este dia',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      const hoy = new Date()
      hoy.setHours(0, 0, 0, 0)
      if (new Date(info.dateStr) < hoy) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No puedes seleccionar fechas pasadas',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      const index = this.fechasSeleccionadas.indexOf(info.dateStr)
      if (index > -1) {
        this.fechasSeleccionadas.splice(index, 1)
      } else {
        this.fechasSeleccionadas.push(info.dateStr)
        this.fechasSeleccionadas.sort()
      }
    },

    handleEventClick(info) {
      // Ignorar eventos de selección (fondo púrpura)
      if (info.event.id.startsWith('seleccion-')) {
        return
      }

      var tipo = info.event.extendedProps && info.event.extendedProps.tipo

      // Eventos de equipo o festivo-equipo: permitir seleccionar el dia
      if (tipo === 'equipo' || tipo === 'festivo-equipo') {
        this.handleDateClick({ date: info.event.start, dateStr: info.event.startStr })
        return
      }

      this.eventoSeleccionado = {
        title: info.event.title,
        start: info.event.startStr,
        extendedProps: info.event.extendedProps,
      }
      // Solo mostrar modal detalle para solicitudes propias
      if (tipo === 'solicitud' || tipo === 'festivo') {
        this.mostrarModalDetalle = true
      }
    },

    handleEventMount(info) {
      var props = info.event.extendedProps
      if (!props) return
      if (props.tipo === 'equipo' || props.tipo === 'festivo-equipo') {
        var esTaller = props.usuario === 'TALLER'
        var harness = info.el.closest('.fc-daygrid-event-harness')
        if (harness) {
          harness.style.width = esTaller ? '99%' : '49%'
          harness.style.maxWidth = esTaller ? '99%' : '49%'
        }
      }
    },

    handleDayCellMount(info) {
      var d = info.date
      var dateStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
      var festivoEquipo = this.festivosEquipoPorFecha[dateStr]
      var festivoMio = this.misFestivosPorFecha[dateStr]
      if (festivoEquipo) {
        this.insertarLabelFestivo(info.el, festivoEquipo, !!festivoMio)
      } else if (festivoMio && typeof festivoMio === 'string') {
        this.insertarLabelFestivo(info.el, festivoMio, true)
      }
    },

    insertarLabelFestivo(el, nombre, esMio) {
      var dayTop = el.querySelector('.fc-daygrid-day-top')
      if (!dayTop) return
      // No duplicar
      if (dayTop.querySelector('.festivo-equipo-label')) return
      var label = document.createElement('span')
      label.className = 'festivo-equipo-label'
      if (esMio) {
        label.textContent = nombre + ' ✓'
        label.style.cssText = 'color: #ff9f43; font-size: 0.65rem; font-weight: 600; margin-left: 5px; font-style: italic;'
      } else {
        label.textContent = nombre
        label.style.cssText = 'color: #a855f7; font-size: 0.65rem; font-weight: 600; margin-left: 5px; font-style: italic;'
      }
      dayTop.appendChild(label)
    },

    pintarFestivosEquipo() {
      var self = this
      // Limpiar labels anteriores
      var container = this.$el
      if (!container) return
      var existentes = container.querySelectorAll('.festivo-equipo-label')
      existentes.forEach(function(el) { el.remove() })

      // Pintar festivos del equipo
      var mapa = this.festivosEquipoPorFecha
      var misMapa = this.misFestivosPorFecha
      if (this.mostrarEquipo) {
        var celdas = container.querySelectorAll('.fc-daygrid-day')
        celdas.forEach(function(celda) {
          var dateAttr = celda.getAttribute('data-date')
          if (dateAttr && mapa[dateAttr]) {
            self.insertarLabelFestivo(celda, mapa[dateAttr], !!misMapa[dateAttr])
          }
        })
      }

      // Pintar festivos propios que no estan en el equipo
      var celdas2 = container.querySelectorAll('.fc-daygrid-day')
      celdas2.forEach(function(celda) {
        var dateAttr = celda.getAttribute('data-date')
        if (dateAttr && misMapa[dateAttr] && typeof misMapa[dateAttr] === 'string' && !mapa[dateAttr]) {
          self.insertarLabelFestivo(celda, misMapa[dateAttr], true)
        }
      })
    },

    getDayCellClass(arg) {
      const classes = []
      // Usar fecha local en vez de UTC para evitar desfase de timezone
      const d = arg.date
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

      if (this.fechasSeleccionadas.includes(dateStr)) {
        classes.push('fecha-seleccionada')
      }

      // Marcar dias festivos
      const esFestivo = this.misFestivos.some(f => {
        const fechaFestivo = typeof f.fecha === 'string' ? f.fecha.split('T')[0] : f.fecha
        return fechaFestivo === dateStr
      })
      if (esFestivo) {
        classes.push('dia-festivo')
      }

      return classes
    },

    quitarFecha(index) {
      this.fechasSeleccionadas.splice(index, 1)
    },

    formatearFecha(fecha) {
      return new Date(fecha).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
      })
    },

    formatearFechaLarga(fecha) {
      return new Date(fecha).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },

    getEstadoVariant(estado) {
      const variantes = {
        pendiente: 'warning',
        aprobada: 'success',
        rechazada: 'danger',
      }
      return variantes[estado] || 'secondary'
    },

    async enviarSolicitud() {
      try {
        await this.crearSolicitud({
          fechas: this.fechasSeleccionadas,
          tipo: this.tipoSolicitud,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Solicitud enviada correctamente',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.fechasSeleccionadas = []
        this.tipoSolicitud = 'completo'
        this.mostrarModalSolicitud = false
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.error || 'Error al enviar solicitud',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async cancelar(id) {
      try {
        await this.cancelarSolicitud({ id, anio: this.anioSeleccionado })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Solicitud cancelada',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.mostrarModalDetalle = false
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.error || 'Error al cancelar solicitud',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style lang="scss">
.vacaciones-calendario {
  // Hero card
  .vac-hero {
    background: linear-gradient(135deg, #7367f0 0%, #5b4ee0 100%);
    border-radius: 0.7rem;
    padding: 0.75rem 1rem;
    color: #fff;
    box-shadow: 0 8px 24px -10px rgba(115, 103, 240, 0.55);
    margin-bottom: 0.85rem;
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: auto minmax(0, 1.4fr) auto auto;
    gap: 1.5rem;
    align-items: center;

    @media (max-width: 991px) {
      grid-template-columns: 1fr auto;
      gap: 0.85rem 1rem;
    }

    @media (max-width: 575px) {
      grid-template-columns: 1fr;
      row-gap: 0.75rem;
    }

    &::after {
      content: '';
      position: absolute;
      top: -60px;
      right: -60px;
      width: 180px;
      height: 180px;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.14) 0%, transparent 70%);
      pointer-events: none;
    }

    &__head {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      position: relative;
      z-index: 1;
    }

    &__icon {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.18);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      flex-shrink: 0;
    }

    &__title {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    &__heading {
      margin: 0;
      color: #fff;
      font-weight: 600;
      font-size: 1rem;
      line-height: 1.15;
    }

    &__sub {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.7rem;
      line-height: 1.2;
    }

    &__main {
      position: relative;
      z-index: 1;
      min-width: 0;

      @media (max-width: 991px) {
        grid-column: 1 / -1;
      }
    }

    &__main-top {
      display: flex;
      align-items: baseline;
      gap: 0.45rem;
      margin-bottom: 0.35rem;
    }

    &__main-num {
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 1;
      color: #fff;
    }

    &__main-label {
      font-size: 0.72rem;
      color: rgba(255, 255, 255, 0.75);
    }

    &__metrics {
      display: flex;
      gap: 0.4rem;
      position: relative;
      z-index: 1;

      @media (max-width: 991px) {
        grid-column: 1 / -1;
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }
  }

  .vac-year-switch {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 999px;
    padding: 4px;
    gap: 2px;

    &__btn {
      width: 28px;
      height: 28px;
      border-radius: 999px;
      border: none;
      background: transparent;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease;

      &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.22);
      }

      &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }
    }

    &__value {
      min-width: 52px;
      text-align: center;
      font-weight: 600;
      font-size: 0.95rem;
      color: #fff;
    }
  }

  .vac-progress {
    height: 5px;
    background: rgba(255, 255, 255, 0.22);
    border-radius: 999px;
    overflow: hidden;
    margin-bottom: 0.3rem;

    &__fill {
      height: 100%;
      background: #fff;
      border-radius: 999px;
      transition: width 0.5s ease;
    }

    &__meta {
      display: flex;
      justify-content: space-between;
      font-size: 0.68rem;
      color: rgba(255, 255, 255, 0.78);
    }
  }

  .vac-metric {
    background: rgba(255, 255, 255, 0.12);
    border-radius: 0.55rem;
    padding: 0.4rem 0.65rem;
    display: flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;

    .feather {
      opacity: 0.85;
      flex-shrink: 0;
    }

    &__txt {
      display: flex;
      flex-direction: column;
      line-height: 1;
      min-width: 0;
    }

    &__num {
      font-size: 1rem;
      font-weight: 700;
      line-height: 1.1;
      color: #fff;
    }

    &__label {
      font-size: 0.64rem;
      color: rgba(255, 255, 255, 0.78);
      text-transform: uppercase;
      letter-spacing: 0.3px;
      margin-top: 1px;
    }
  }

  // Toolbar
  .vac-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0.65rem 1rem;
    background: #fff;
    border-radius: 0.6rem;
    box-shadow: 0 2px 12px -4px rgba(34, 41, 47, 0.08);
    margin-bottom: 1rem;

    &__filters {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__label {
      font-size: 0.72rem;
      color: #9b98a7;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin-right: 0.15rem;
    }

    &__action {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
  }

  .vac-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.4rem 0.85rem;
    border: 1.5px solid #ebe9f1;
    border-radius: 999px;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 999px;
      display: inline-block;
    }

    &:hover {
      border-color: #d0cdde;
      color: #5e5873;
    }

    &--active {
      border-color: transparent;
      font-weight: 600;
    }

    &--equipo.vac-pill--active {
      background: rgba(99, 102, 241, 0.12);
      color: #6366f1;
    }

    &--tareas.vac-pill--active {
      background: rgba(20, 184, 166, 0.12);
      color: #14b8a6;
    }
  }

  .vac-toolbar__hint {
    display: inline-flex;
    align-items: center;
    font-size: 0.78rem;
    color: #9b98a7;
  }

  // Botón flotante
  .vac-fab {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
    padding: 0.55rem 0.75rem 0.55rem 1.1rem;
    background: #2f3342;
    color: #fff;
    border-radius: 12px;
    box-shadow: 0 12px 32px -8px rgba(15, 23, 42, 0.45);
    z-index: 100;
    min-width: 380px;

    &__left {
      display: flex;
      align-items: center;
      gap: 0.55rem;
    }

    &__count {
      font-size: 1rem;
      font-weight: 700;
      color: #a79ff5;
      font-variant-numeric: tabular-nums;
      line-height: 1;
    }

    &__label {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.7);
    }

    &__clear {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.55rem;
      background: rgba(255, 255, 255, 0.1);
      border: 0;
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.75);
      font-size: 0.7rem;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
      margin-left: 0.4rem;

      &:hover {
        background: rgba(255, 255, 255, 0.18);
        color: #fff;
      }
    }

    &__action {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.55rem 1rem;
      background: linear-gradient(135deg, #7367f0 0%, #5b4ee0 100%);
      border: 0;
      border-radius: 8px;
      color: #fff;
      font-size: 0.82rem;
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.15s ease, box-shadow 0.15s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px -4px rgba(115, 103, 240, 0.5);
      }
    }
  }

  .vac-fab-enter-active,
  .vac-fab-leave-active {
    transition: opacity 250ms ease, transform 300ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .vac-fab-enter,
  .vac-fab-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(24px);
  }


  .full-calendar {
    // Header del calendario
    .fc-toolbar {
      flex-wrap: wrap;
      gap: 0.5rem;

      .fc-toolbar-title {
        font-size: 1.25rem;
        font-weight: 600;
        text-transform: capitalize;
      }

      .fc-button {
        padding: 0.4rem 0.8rem;
        font-size: 0.9rem;
        border-radius: 0.358rem;
        text-transform: capitalize;

        &.fc-button-primary {
          background-color: #7367f0;
          border-color: #7367f0;

          &:hover {
            background-color: #5e50ee;
            border-color: #5e50ee;
          }

          &:disabled {
            background-color: #7367f0;
            border-color: #7367f0;
            opacity: 0.65;
          }
        }

        &.fc-button-active {
          background-color: #5e50ee !important;
          border-color: #5e50ee !important;
        }
      }
    }

    // Cabecera dias de la semana
    .fc-col-header-cell {
      padding: 0.75rem 0;
      background-color: #f8f8f8;
      border-color: #ebe9f1;

      .fc-col-header-cell-cushion {
        font-weight: 600;
        color: #5e5873;
        text-transform: uppercase;
        font-size: 0.75rem;
      }
    }

    // Celdas de dias
    .fc-daygrid-day {
      transition: background-color 0.15s ease;
      border-color: #ebe9f1;

      &:hover {
        background-color: rgba(115, 103, 240, 0.04);
      }

      &.fecha-seleccionada {
        background-color: rgba(115, 103, 240, 0.15) !important;

        .fc-daygrid-day-number {
          font-weight: 600;
          color: #7367f0;
        }
      }

      .fc-bg-event {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        z-index: 0 !important;
      }

      &.fc-day-today {
        background-color: rgba(115, 103, 240, 0.08) !important;

        .fc-daygrid-day-number {
          background-color: #7367f0;
          color: #fff;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      &.fc-day-other {
        background-color: #fafafa;

        .fc-daygrid-day-number {
          color: #b9b9c3;
        }
      }

      // Sábados y domingos - gris oscuro, no seleccionables
      &.fc-day-sat,
      &.fc-day-sun {
        background-color: #e0e0e0;
        cursor: not-allowed;

        .fc-daygrid-day-number {
          color: #999;
        }

        &:hover {
          background-color: #e0e0e0;
        }
      }

      // Dias festivos
      &.dia-festivo {
      }
    }

    .fc-daygrid-day-number {
      cursor: pointer;
      padding: 0.5rem;
      font-size: 0.9rem;
      color: #6e6b7b;
    }

    // Layout flex para eventos (como en admin)
    .fc-daygrid-day-events {
      display: flex !important;
      flex-wrap: wrap !important;
      align-content: flex-start !important;
      justify-content: flex-start !important;
      gap: 2px !important;
      padding: 2px !important;
      position: relative !important;
      margin: 0 !important;
    }

    .fc-daygrid-day-events > .fc-daygrid-event-harness:empty {
      display: none !important;
    }

    .fc-daygrid-day-events > .fc-daygrid-event-harness {
      position: relative !important;
      margin: 0 !important;
      padding: 0 !important;
      width: 99%;
      max-width: 99%;
      min-width: 0 !important;
      inset: unset !important;
      top: unset !important;
      left: unset !important;
      right: unset !important;
      flex-shrink: 0 !important;

      &:first-child {
        margin-left: -2px !important;
      }
    }

    .fc-daygrid-event-harness:has(.evento-taller) {
      width: 99% !important;
      max-width: 99% !important;
    }

    .fc-daygrid-day-frame {
      min-height: 100px;
    }

    .fc-daygrid-day-events > .fc-daygrid-bg-harness {
      position: absolute !important;
      width: 100% !important;
      max-width: 100% !important;
      inset: 0 !important;
      pointer-events: none !important;
    }

    // Eventos
    .fc-daygrid-event {
      position: relative !important;
      border-radius: 0.25rem;
      padding: 2px 4px !important;
      font-size: 0.75rem;
      margin: 0 !important;
      inset: unset !important;
      width: 100% !important;
      left: 0 !important;
      right: 0 !important;
      box-sizing: border-box !important;
      border: none !important;

      .fc-event-main {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .fc-event-title {
        font-weight: 500;
      }
    }

    .fc-daygrid-day-bottom {
      width: 100% !important;
      max-width: 100% !important;
    }

    .fc-daygrid-more-link {
      font-size: 0.75rem;
      color: #7367f0;
      font-weight: 500;
    }

    // Bordes generales
    .fc-scrollgrid {
      border-color: #ebe9f1;
      border-radius: 0.428rem;
      overflow: hidden;
    }

    .fc-scrollgrid-section > * {
      border-color: #ebe9f1;
    }
  }

  // Leyenda agrupada
  .vac-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem 2.25rem;
    padding: 1rem 0.25rem 0.25rem;
    margin-top: 1rem;
    border-top: 1px solid #ebe9f1;

    &__group {
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
    }

    &__title {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.68rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #9b98a7;
    }

    &__items {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem 0.85rem;
    }

    &__item {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      font-size: 0.78rem;
      color: #6e6b7b;
    }

    &__color {
      width: 10px;
      height: 10px;
      border-radius: 3px;
      display: inline-block;
    }
  }
}

// Dark mode support
.dark-layout {
  .vacaciones-calendario {
    .vac-toolbar {
      background: #283046;
      box-shadow: 0 2px 12px -4px rgba(0, 0, 0, 0.35);
    }

    .vac-toolbar__label {
      color: #b4b7bd;
    }

    .vac-pill {
      border-color: #3b4253;
      color: #b4b7bd;

      &:hover {
        border-color: #4d5466;
        color: #d0d2d6;
      }

      &--active {
        border-color: transparent;
      }

      &--equipo.vac-pill--active {
        background: rgba(99, 102, 241, 0.22);
        color: #a5a8ff;
      }

      &--tareas.vac-pill--active {
        background: rgba(20, 184, 166, 0.22);
        color: #5eead4;
      }
    }

    .vac-toolbar__hint {
      color: #6e6b7b;
    }

    .vac-fab {
      background: #1e2536;
      box-shadow: 0 12px 32px -8px rgba(0, 0, 0, 0.6);
    }

    .vac-legend {
      border-top-color: #3b4253;

      &__title {
        color: #6e6b7b;
      }

      &__item {
        color: #b4b7bd;
      }
    }

    .full-calendar {
      .fc-col-header-cell {
        background-color: #283046;
        border-color: #3b4253;

        .fc-col-header-cell-cushion {
          color: #b4b7bd;
        }
      }

      .fc-daygrid-day {
        border-color: #3b4253;

        &:hover {
          background-color: rgba(115, 103, 240, 0.08);
        }

        &.fc-day-today {
          background-color: rgba(115, 103, 240, 0.15) !important;
        }

        &.fc-day-other {
          background-color: #242b3d;
        }

        &.fc-day-sat,
        &.fc-day-sun {
          background-color: #1e2536;

          .fc-daygrid-day-number {
            color: #6b7280;
          }

          &:hover {
            background-color: #1e2536;
          }
        }

        &.dia-festivo {
        }
      }

      .fc-daygrid-day-number {
        color: #b4b7bd;
      }

      .fc-scrollgrid {
        border-color: #3b4253;
      }
    }
  }
}


.fc-next-button{
  margin-left: 10px !important;
}
</style>
